import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Drawer,
  Dropdown,
  Form,
  Modal,
  Spin,
  Tag,
  Tooltip,
  Typography
} from 'antd';
import type { MenuProps, TableColumnsType } from 'antd';
import {
  ApartmentOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  InfoCircleOutlined,
  MailOutlined,
  MoreOutlined,
  PlusOutlined,
  ShoppingCartOutlined,
  StarFilled,
  TeamOutlined,
  UserOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { authService } from '../services/auth';
import { isMarketing } from '../modules/marketing/marketingAccessPolicy';
import CustomerForm from '../components/customers/CustomerForm';
import CustomerProductPricesTab from '../components/customers/CustomerProductPricesTab';
import { applyCustomerSaveError } from '../components/customers/customerFormErrors';
import {
  canCreateCustomers as canCreateCustomersForUser,
  canDeleteCustomers as canDeleteCustomersForUser,
  canEditCustomers as canEditCustomersForUser,
  canListCustomers as canListCustomersForUser,
} from '../modules/customers/customerAccessPolicy';
import { createCustomer } from '../modules/customers/api/customersApi';
import { deleteCustomerWithFeedback } from '../modules/customers/customerDelete';
import type { Customer, CustomerSubmitValues } from '../modules/customers/types';
import { normalizeCustomerContacts, type CustomerContact } from '../utils/customerContacts';
import { DEFAULT_CUSTOMER_PAYMENT_TERM_DAYS } from '../modules/orders/customerPayment';
import { getVatState } from '../utils/customerVatStatus';
import { useFeedback } from '../utils/feedback';
import { useBackNavigation } from '../hooks/useBackNavigation';
import {
  ObjectCardEmpty,
  ObjectCardFacts,
  ObjectCardHero,
  ObjectCardSection,
  type ObjectCardFact,
  ObjectCardTable
} from '../components/layout/ObjectCard';
import './CustomerDetail.css';

const { Text } = Typography;

const EMPTY_VALUE = '—';
/** GET /customers/:id returns the 10 most recent orders the user may see. */
const RECENT_ORDERS_LIMIT = 10;

interface CustomerOrderSummary {
  id: string;
  orderNumber?: string;
  status?: string;
  createdAt?: string;
}

type CustomerDetailData = Customer & { orders?: CustomerOrderSummary[] };

type ContactRow = CustomerContact & { id: string };

const formatDateTime = (value?: string | null) => (value ? new Date(value).toLocaleString('pl-PL') : EMPTY_VALUE);

/**
 * Customer card on the object-card primitives (components/layout/ObjectCard,
 * owner 2026-10-19): hero with monogram, status, address/e-mail line and facts
 * strip; contacts, orders and prices in the main column, company data and
 * branches aside. No tabs; editing opens in a side drawer.
 */
const CustomerDetail: React.FC = () => {
  const { t } = useTranslation(['customers', 'common', 'orders']);
  const feedback = useFeedback();
  const navigate = useNavigate();
  const goBack = useBackNavigation();
  const { id } = useParams<{ id: string }>();
  // `?edit=1` opens the card straight in edit mode, e.g. from the WZ form's
  // „Uzupełnij kartę klienta” link when the customer has no address.
  const [searchParams] = useSearchParams();
  const openInEditMode = searchParams.get('edit') === '1';
  const [customer, setCustomer] = useState<CustomerDetailData | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editParamApplied, setEditParamApplied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [creatingBranch, setCreatingBranch] = useState(false);
  const [branchSubmitting, setBranchSubmitting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [form] = Form.useForm();
  const [branchForm] = Form.useForm();

  const currentUser = authService.getCurrentUser();
  const canCreateCustomers = canCreateCustomersForUser(currentUser);
  const canEditCustomers = canEditCustomersForUser(currentUser);
  const canDeleteCustomers = canDeleteCustomersForUser(currentUser);
  const canListCustomers = canListCustomersForUser(currentUser);
  const canViewOrders = !isMarketing(currentUser);
  const canViewCustomerPayments = authService.canViewCustomerPayments();
  const canViewCustomerSalePrices = authService.canViewCustomerSalePrices();
  const canEditCustomerSalePrices = authService.canEditCustomerSalePrices();

  const fetchCustomer = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [detailResponse, listResponse] = await Promise.all([
        api.get(`/customers/${id}`),
        canListCustomers
          ? api.get('/customers?status=all')
          : Promise.resolve({ data: [] })
      ]);
      setCustomer(detailResponse.data);
      setCustomers(listResponse.data);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        feedback.warning(t('customers:messages.notFound'));
        navigate('/app/customers', { replace: true });
        return;
      }
      feedback.error(t('customers:messages.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!openInEditMode || editParamApplied || !customer || customer.deletedAt) return;
    setEditParamApplied(true);
    if (canEditCustomers) setEditing(true);
  }, [openInEditMode, editParamApplied, customer, canEditCustomers]);

  const parentCustomer = useMemo(
    () =>
      customer?.parentCustomerId
        ? customers.find((item) => item.id === customer.parentCustomerId) || null
        : null,
    [customer, customers]
  );

  const contacts = useMemo<ContactRow[]>(
    () => (customer
      ? normalizeCustomerContacts(customer.contactPeople, customer.contactPerson, customer.phone)
          .map((contact, index) => ({ ...contact, id: `${customer.id}-contact-${index}` }))
      : []),
    [customer]
  );

  const branches = useMemo(
    () =>
      customer
        ? customers
            .filter((item) => item.parentCustomerId === customer.id && !item.deletedAt)
            .sort((left, right) =>
              left.name.localeCompare(right.name, 'pl', { sensitivity: 'base' })
            )
        : [],
    [customer, customers]
  );

  const handleUpdate = async (values: CustomerSubmitValues) => {
    if (!id) return;
    setSubmitting(true);
    form.setFields([
      { name: 'name', errors: [] },
      { name: 'contactPeople', errors: [] },
      { name: 'taxId', errors: [] },
      { name: 'regon', errors: [] },
      { name: 'parentCustomerId', errors: [] }
    ]);

    try {
      await api.put(`/customers/${id}`, values);
      feedback.success(t('customers:messages.updateSuccess', { name: values.name }));
      setEditing(false);
      await fetchCustomer();
    } catch (error: any) {
      applyCustomerSaveError(error, form, t, feedback);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateBranch = async (values: CustomerSubmitValues) => {
    setBranchSubmitting(true);
    branchForm.setFields([
      { name: 'name', errors: [] },
      { name: 'contactPeople', errors: [] },
      { name: 'taxId', errors: [] },
      { name: 'regon', errors: [] },
      { name: 'parentCustomerId', errors: [] }
    ]);

    try {
      const createdBranch = await createCustomer(values);
      feedback.success(t('customers:messages.createSuccess', { name: values.name }));
      setCreatingBranch(false);
      branchForm.resetFields();

      if (createdBranch?.id) {
        navigate(`/app/customers/${createdBranch.id}`);
      } else {
        await fetchCustomer();
      }
    } catch (error: any) {
      applyCustomerSaveError(error, branchForm, t, feedback);
    } finally {
      setBranchSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!customer) return;
    setDeleting(true);
    const outcome = await deleteCustomerWithFeedback(customer, t, feedback);
    setDeleting(false);
    if (outcome === 'failed') return;
    setConfirmingDelete(false);
    if (outcome === 'deleted') {
      navigate('/app/customers', { replace: true });
    } else {
      await fetchCustomer();
    }
  };

  if (loading) {
    return (
      <div className="obj-loading">
        <Spin size="large" />
      </div>
    );
  }

  if (!customer) {
    return null;
  }

  const archived = Boolean(customer.deletedAt);
  const orders = customer.orders || [];
  const ordersCount = orders.length >= RECENT_ORDERS_LIMIT ? `${RECENT_ORDERS_LIMIT}+` : String(orders.length);

  const renderValue = (value?: string | null) =>
    value ? value : <Text type="secondary">{EMPTY_VALUE}</Text>;

  const renderVatStatus = () => {
    const state = getVatState(customer);
    if (state === 'unchecked') {
      return <Text type="secondary">{t('customers:validation.notChecked')}</Text>;
    }
    return state === 'valid' ? (
      <Tag color="success" icon={<CheckCircleOutlined />}>
        {t('customers:table.valid')}
      </Tag>
    ) : (
      <Tag color="error" icon={<CloseCircleOutlined />}>
        {t('customers:table.invalid')}
      </Tag>
    );
  };

  const moreItems: NonNullable<MenuProps['items']> = [];
  if (canDeleteCustomers && !archived) {
    moreItems.push({
      key: 'delete',
      icon: <DeleteOutlined />,
      label: t('customers:detail.delete'),
      danger: true
    });
  }

  const contactColumns: TableColumnsType<ContactRow> = [
    {
      title: t('customers:form.contactPerson'),
      key: 'name',
      render: (_, contact) => (
        <span className="customer-card__contact-name">
          {contact.isPrimary && (
            <Tooltip title={t('customers:form.contactPrimary')}>
              <StarFilled
                className="customer-card__contact-primary"
                aria-label={t('customers:form.contactPrimary')}
                data-testid="customer-contact-primary"
              />
            </Tooltip>
          )}
          <span className="obj-strong">{contact.name || EMPTY_VALUE}</span>
          {contact.position && <span className="customer-card__contact-position"> · {contact.position}</span>}
        </span>
      )
    },
    {
      title: t('customers:form.phone'),
      dataIndex: 'phone',
      key: 'phone',
      width: 136,
      render: (value?: string | null) => renderValue(value)
    },
    {
      title: t('customers:detail.chips.email'),
      dataIndex: 'email',
      key: 'email',
      render: (value?: string | null) => renderValue(value)
    }
  ];

  const orderColumns: TableColumnsType<CustomerOrderSummary> = [
    {
      title: t('orders:table.orderNumber'),
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      render: (value: string | undefined, record) => (
        <Button type="link" className="obj-link" onClick={() => navigate(`/app/orders/${record.id}`)}>
          {value || record.id}
        </Button>
      )
    },
    {
      title: t('orders:table.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string | undefined) =>
        status ? <Tag>{t(`orders:statusOptions.${status}`)}</Tag> : EMPTY_VALUE
    },
    {
      title: t('customers:preview.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value: string | undefined) => formatDateTime(value)
    }
  ];

  const facts: ObjectCardFact[] = [
    ...(customer.isBranch ? [{
      key: 'parent',
      label: t('customers:preview.parentCustomer'),
      icon: <ApartmentOutlined />,
      value: parentCustomer ? (
        <Button type="link" className="obj-link" onClick={() => navigate(`/app/customers/${parentCustomer.id}`)}>
          {parentCustomer.name}
        </Button>
      ) : EMPTY_VALUE,
      testId: 'customer-chip-parent'
    }] : []),
    { key: 'taxId', label: t('customers:preview.taxId'), icon: <IdcardOutlined />, value: customer.taxId || EMPTY_VALUE, testId: 'customer-chip-tax-id' },
    ...(canViewCustomerPayments ? [{
      key: 'paymentTerm',
      label: t('customers:detail.chips.paymentTerm'),
      icon: <CalendarOutlined />,
      value: t('customers:preview.paymentTermDays', {
        count: customer.defaultPaymentTermDays ?? DEFAULT_CUSTOMER_PAYMENT_TERM_DAYS
      }),
      testId: 'customer-chip-payment-term'
    }] : []),
    ...(canViewOrders ? [{
      key: 'orders',
      label: t('customers:detail.chips.orders'),
      icon: <ShoppingCartOutlined />,
      value: (
        <Tooltip title={orders.length >= RECENT_ORDERS_LIMIT ? t('customers:detail.recentOrdersHint', { count: RECENT_ORDERS_LIMIT }) : undefined}>
          <span>{ordersCount}</span>
        </Tooltip>
      ),
      testId: 'customer-chip-orders'
    }] : []),
    { key: 'contacts', label: t('customers:preview.contactSection'), icon: <TeamOutlined />, value: contacts.length, testId: 'customer-chip-contacts' }
  ];

  return (
    <div className="obj-card customer-card" data-testid="customer-card">
      <ObjectCardHero
        testId="customer-card-header"
        kind={customer.isBranch ? t('customers:preview.branch') : t('customers:detail.kind')}
        name={customer.name}
        backLabel={t('customers:detail.back')}
        onBack={() => goBack('/app/customers')}
        badges={(
          <span
            className={`obj-pill ${archived ? '' : 'obj-pill--success'}`}
            data-testid="customer-status-pill"
          >
            {archived ? t('customers:detail.statusArchived') : t('customers:detail.statusActive')}
          </span>
        )}
        meta={[
          customer.address && <span data-testid="customer-chip-address"><EnvironmentOutlined aria-hidden="true" /> {customer.address}</span>,
          customer.email && <span data-testid="customer-chip-email"><MailOutlined aria-hidden="true" /> <a href={`mailto:${customer.email}`}>{customer.email}</a></span>
        ]}
        actions={((canEditCustomers && !archived) || moreItems.length > 0) && (
          <>
            {moreItems.length > 0 && (
              <Dropdown
                menu={{ items: moreItems, onClick: ({ key }) => key === 'delete' && setConfirmingDelete(true) }}
                trigger={['click']}
              >
                <Button icon={<MoreOutlined />} data-testid="customer-more-actions">
                  {t('customers:detail.more')}
                </Button>
              </Dropdown>
            )}
            {canEditCustomers && !archived && (
              <Button type="primary" icon={<EditOutlined />} onClick={() => setEditing(true)}>
                {t('common:edit')}
              </Button>
            )}
          </>
        )}
        facts={facts}
        alerts={archived && <Alert type="info" showIcon message={t('customers:preview.archivedNotice')} />}
      />

      <div className="obj-card__body">
          <ObjectCardSection
            testId="customer-section-company" tone="gray"
            title={t('customers:preview.companySection')}
            icon={<InfoCircleOutlined />}
          >
            <ObjectCardFacts
              items={[
                { label: t('customers:preview.customerType'), value: customer.isBranch ? t('customers:preview.branch') : t('customers:preview.company') },
                { label: t('customers:preview.regon'), value: renderValue(customer.regon) },
                { label: t('customers:preview.vatStatus'), value: renderVatStatus() },
                { label: t('customers:fields.category'), value: renderValue(customer.customerCategoryName) },
                { label: t('customers:preview.createdAt'), value: formatDateTime(customer.createdAt) },
                { label: t('customers:preview.updatedAt'), value: formatDateTime(customer.updatedAt) },
                ...(archived ? [{ label: t('customers:preview.archivedAt'), value: formatDateTime(customer.deletedAt) }] : [])
              ]}
            />
          </ObjectCardSection>

          <div className="obj-card__col">
            <ObjectCardSection
              testId="customer-section-contacts" tone="green"
              title={t('customers:preview.contactSection')}
              icon={<TeamOutlined />}
              count={contacts.length}
            >
              {contacts.length > 0 ? (
                <ObjectCardTable<ContactRow>
                  size="small"
                  columns={contactColumns}
                  dataSource={contacts}
                  rowKey="id"
                  pagination={false}
                />
              ) : (
                <ObjectCardEmpty
                  icon={<UserOutlined />}
                  text={t('customers:detail.noContacts')}
                  action={canEditCustomers && !archived && (
                    <Button size="small" icon={<PlusOutlined />} onClick={() => setEditing(true)}>
                      {t('customers:form.addContact')}
                    </Button>
                  )}
                />
              )}
            </ObjectCardSection>

            {!customer.isBranch && (
              <ObjectCardSection
                testId="customer-section-branches" tone="indigo"
                title={t('customers:detail.branchesTitle')}
                icon={<ApartmentOutlined />}
                count={branches.length}
                actions={canCreateCustomers && !archived && branches.length > 0 && (
                  <Tooltip title={!customer.taxId ? t('customers:detail.branchRequiresTaxId') : undefined}>
                    <span>
                      <Button size="small" icon={<PlusOutlined />} disabled={!customer.taxId} onClick={() => setCreatingBranch(true)}>
                        {t('customers:detail.addBranch')}
                      </Button>
                    </span>
                  </Tooltip>
                )}
              >
                {branches.length > 0 ? (
                  <ul className="customer-card__branches">
                    {branches.map((branch) => (
                      <li key={branch.id}>
                        <Button type="link" className="obj-link" onClick={() => navigate(`/app/customers/${branch.id}`)}>
                          {branch.name}
                        </Button>
                        <span>{branch.address || EMPTY_VALUE}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <ObjectCardEmpty
                    text={t('customers:detail.noBranches')}
                    action={canCreateCustomers && !archived && (
                      <Tooltip title={!customer.taxId ? t('customers:detail.branchRequiresTaxId') : undefined}>
                        <span>
                          <Button size="small" icon={<PlusOutlined />} disabled={!customer.taxId} onClick={() => setCreatingBranch(true)}>
                            {t('customers:detail.addBranch')}
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  />
                )}
              </ObjectCardSection>
            )}
          </div>

          {canViewOrders && (
            <ObjectCardSection
              wide
              testId="customer-section-orders" tone="orange"
              title={t('customers:detail.ordersTab')}
              icon={<ShoppingCartOutlined />}
              count={ordersCount}
              hint={orders.length >= RECENT_ORDERS_LIMIT
                ? t('customers:detail.recentOrdersHint', { count: RECENT_ORDERS_LIMIT })
                : undefined}
            >
              {orders.length > 0 ? (
                <ObjectCardTable<CustomerOrderSummary>
                  size="small"
                  columns={orderColumns}
                  dataSource={orders}
                  rowKey="id"
                  pagination={false}
                />
              ) : (
                <ObjectCardEmpty icon={<ShoppingCartOutlined />} text={t('customers:detail.noOrders')} />
              )}
            </ObjectCardSection>
          )}



          {canViewCustomerSalePrices && (
            <CustomerProductPricesTab customerId={customer.id} canEdit={canEditCustomerSalePrices} />
          )}
      </div>

      <Drawer
        title={t('customers:editCustomer')}
        open={editing}
        onClose={() => setEditing(false)}
        width={640}
        rootClassName="obj-drawer"
        maskClosable={false}
        destroyOnClose
      >
        <CustomerForm
          form={form}
          customers={customers}
          editingCustomer={customer}
          canViewCustomerPayments={canViewCustomerPayments}
          submitting={submitting}
          submitLabel={t('common:save')}
          onSubmit={handleUpdate}
          open={editing}
          onCancel={() => setEditing(false)}
        />
      </Drawer>

      <Modal
        title={t('customers:detail.createBranchTitle', { name: customer.name })}
        open={creatingBranch}
        onCancel={() => setCreatingBranch(false)}
        maskClosable={false}
        footer={null}
        width={600}
        destroyOnClose
      >
        <CustomerForm
          form={branchForm}
          customers={customers}
          editingCustomer={null}
          initialBranchParent={customer}
          canViewCustomerPayments={canViewCustomerPayments}
          submitting={branchSubmitting}
          submitLabel={t('customers:detail.addBranch')}
          onSubmit={handleCreateBranch}
          open={creatingBranch}
          onCancel={() => setCreatingBranch(false)}
        />
      </Modal>

      <Modal
        title={t('customers:confirmDeleteCustomer')}
        open={confirmingDelete}
        onCancel={() => !deleting && setConfirmingDelete(false)}
        onOk={handleDelete}
        okText={t('common:yes')}
        cancelText={t('common:no')}
        okButtonProps={{ danger: true }}
        confirmLoading={deleting}
      >
        <p>{t('customers:confirmDeleteDescription')}</p>
      </Modal>
    </div>
  );
};

export default CustomerDetail;
